/* eslint-disable @typescript-eslint/no-use-before-define */
//* eslint-disable camelcase */
/* eslint-disable no-param-reassign */
import {
  createSlice,
  createSelector,
  PayloadAction,
  createAsyncThunk
} from '@reduxjs/toolkit';
import {storesApi} from '../api/storesApi';
import {
  StoreDetails,
  StoreImageType,
  StorePendingDetailKey,
  PendingStoreDetails,
  BannerFillType,
  OnlineStoreType,
  CampaignAttributes
} from '../models/storeDetails';
import {StoreProduct} from '../models/storeProduct';
import {Product, Color} from '../models/product';
import {productsApi} from '../api/productsApi';
import type {AppDispatch, RootState} from '../store';
import {getProductColors} from '../utils/helpers';

type ColorMapType = Record<string, Color[]>;

export type ImageObjectUrlsType = {
  [key in StoreImageType]?: string;
};

export interface StoresState {
  store: null | string;
  storeDetails: null | StoreDetails;
  storeProducts: null | StoreProduct[];
  mmsProducts: null | Product[];
  pendingDetails: PendingStoreDetails;
  localImages: ImageObjectUrlsType;
}

const initialState: StoresState = {
  store: null,
  storeDetails: null,
  storeProducts: null,
  mmsProducts: null,
  pendingDetails: {},
  localImages: {}
};

// Thunks

export const addLocalImage = createAsyncThunk<
  {url: string; imageType: StoreImageType},
  {file: File; imageType: StoreImageType},
  {dispatch: AppDispatch; state: RootState}
>(
  'stores/addLocalImage',
  async ({file, imageType}: {file: File; imageType: StoreImageType}) => {
    const url = URL.createObjectURL(file);
    return {url, imageType};
  }
);

export const removeLocalImage = createAsyncThunk<
  StoreImageType,
  StoreImageType,
  {dispatch: AppDispatch; state: RootState}
>('stores/removeLocalImage', async (imageType: StoreImageType, thunkApi) => {
  const url = thunkApi.getState().stores.localImages[imageType];
  if (url) {
    URL.revokeObjectURL(url);
  }
  return imageType;
});

export const removeLocalImages = createAsyncThunk<
  void,
  void,
  {dispatch: AppDispatch; state: RootState}
>('stores/removeLocalImages', async (payload: void, thunkApi) => {
  const {localImages} = thunkApi.getState().stores;
  const imageKeys = Object.keys(localImages);
  imageKeys.forEach(key => {
    const url = localImages[key as keyof ImageObjectUrlsType];
    if (url) {
      URL.revokeObjectURL(url);
    }
  });
});

export const addPendingDetail = createAsyncThunk<
  void,
  {key: StorePendingDetailKey; val: unknown},
  {dispatch: AppDispatch; state: RootState}
>(
  'stores/addPendingDetail',
  async ({key, val}: {key: StorePendingDetailKey; val: unknown}, thunkApi) => {
    const {getState, dispatch} = thunkApi;
    const {pendingDetails, storeDetails} = getState().stores;

    if (key !== 'logo' && key !== 'heroImage' && storeDetails[key] === val) {
      if (key in pendingDetails) dispatch(deletePendingDetail(key));
    } else {
      dispatch(setPendingDetail({key, val}));
    }
  }
);

export const removePendingDetail = createAsyncThunk<
  void,
  StorePendingDetailKey,
  {dispatch: AppDispatch; state: RootState}
>(
  'stores/removePendingDetail',
  async (key: StorePendingDetailKey, thunkApi) => {
    const {dispatch} = thunkApi;
    if (key === 'logo' || key === 'heroImage') {
      dispatch(removeLocalImage(key));
    }
    dispatch(deletePendingDetail(key));
  }
);

export const removePendingDetails = createAsyncThunk<
  void,
  void,
  {dispatch: AppDispatch; state: RootState}
>('stores/removePendingDetails', async (payload: null, thunkApi) => {
  const {dispatch} = thunkApi;
  dispatch(removeLocalImages());
  dispatch(deletePendingDetails());
});

// Slice

export const storesSlice = createSlice({
  name: 'stores',
  initialState,
  reducers: {
    setPendingDetail(
      state,
      action: PayloadAction<{key: StorePendingDetailKey; val: unknown}>
    ) {
      const {key, val} = action.payload;
      const newDetails = {
        ...state.pendingDetails,
        [key]: val
      };

      state.pendingDetails = newDetails;
    },
    deletePendingDetail(state, action: PayloadAction<StorePendingDetailKey>) {
      delete state.pendingDetails[action.payload];
    },
    deletePendingDetails(state) {
      state.pendingDetails = {};
    }
  },
  extraReducers: builder => {
    builder
      .addCase(addLocalImage.fulfilled, (state, {payload}) => {
        state.localImages[payload.imageType] = payload.url;
      })
      .addCase(removeLocalImage.fulfilled, (state, {payload}) => {
        delete state.localImages[payload];
        delete state.pendingDetails[payload];
      })
      .addCase(removeLocalImages.fulfilled, state => {
        state.localImages = {};
      })
      .addMatcher(
        storesApi.endpoints.getStoreDetails.matchFulfilled,
        (state, {payload}) => {
          state.store = payload?.name;
          state.storeDetails = payload;
        }
      )
      .addMatcher(
        storesApi.endpoints.getStoreProducts.matchFulfilled,
        (state, {payload}) => {
          state.storeProducts = payload;
        }
      )
      .addMatcher(
        productsApi.endpoints.fetchProducts.matchFulfilled,
        (state, {payload}) => {
          state.mmsProducts = payload;
        }
      );
  }
});

// Selectors

export const selectPendingDetailsCount = createSelector(
  (state: RootState) => state.stores.pendingDetails,
  (pendingDetails: PendingStoreDetails): number =>
    Object.keys(pendingDetails).length
);

export const selectBannerFillType = createSelector(
  (state: RootState) => state.stores.pendingDetails,
  (state: RootState) => state.stores.storeDetails,
  (
    pendingDetails: PendingStoreDetails,
    storeDetails: StoreDetails
  ): BannerFillType => {
    // Will need additional cases to support fillColor and custom options
    const visibility =
      pendingDetails?.heroLogoVisibility || storeDetails?.heroLogoVisibility;
    if (visibility === 'hero_background') return 'image';
    return 'none';
  }
);

export const selectStoreType = createSelector(
  (state: RootState) => state.stores.storeDetails,
  (storeDetails: StoreDetails): OnlineStoreType => {
    if (!storeDetails) return 'loading';
    const onlineStoreType = storeDetails.eventStore ? 'event' : 'selfServe';
    return onlineStoreType;
  }
);

export const selectPrimaryCampaign = createSelector(
  (state: RootState) => state.stores.storeDetails?.campaigns,
  (state: RootState) => state.stores.storeDetails?.name,
  (
    campaigns: CampaignAttributes[],
    name: string
  ): CampaignAttributes | null => {
    if (!campaigns || campaigns.length === 0) return null;

    const index = campaigns
      .map(campaign => {
        return campaign.name;
      })
      .indexOf(name);
    return index === -1 ? campaigns[0] : campaigns[index];
  }
);

export const selectReplacementCampaign = createSelector(
  (state: RootState) => state.stores.storeDetails?.campaigns,
  (campaigns: CampaignAttributes[]): CampaignAttributes | null => {
    if (!campaigns || campaigns.length === 0) return null;

    const activeReplacementCampaign = campaigns.filter(
      campaign => campaign.replacement === true && campaign.status === 'active'
    );

    if (activeReplacementCampaign.length === 0) return null;

    return activeReplacementCampaign.pop();
  }
);

export const selectProductColorMap = createSelector(
  [
    (state: RootState) => state.stores.mmsProducts,
    (state: RootState) => state.stores.storeProducts
  ],
  (mmsProducts, storeProducts) =>
    mmsProducts && storeProducts
      ? storeProducts?.reduce((acc: ColorMapType, storeProduct) => {
          const colors = getProductColors(storeProduct, mmsProducts);
          acc[storeProduct.id] = colors;

          return acc;
        }, {})
      : ({} as ColorMapType)
);

export const {setPendingDetail, deletePendingDetails, deletePendingDetail} =
  storesSlice.actions;

export const {reducer} = storesSlice;
